/**
 * ThemeControl (docs/04 §1b) — Light / Dark / Device, as a segmented control.
 *
 * Three buttons rather than the usual sun-moon toggle, because the brand
 * standard requires "use device setting" as a first-class option and a two-state
 * toggle cannot express it: a person following their laptop's schedule would
 * have to pick a side and lose the schedule. Shaped exactly like
 * DensityControl — same group semantics, same `aria-pressed` toggles, same
 * label-hides-below-md behaviour — so the two preferences in the account menu
 * read as one pair and not two inventions.
 */

import { type ThemePreference } from '@propos/shared';
import { clsx } from 'clsx';
import { Monitor, Moon, Sun } from 'lucide-react';
import type { ReactNode } from 'react';

const OPTIONS: readonly {
  readonly value: ThemePreference;
  readonly label: string;
  readonly icon: ReactNode;
}[] = [
  { value: 'light', label: 'Light', icon: <Sun size={15} aria-hidden /> },
  { value: 'dark', label: 'Dark', icon: <Moon size={15} aria-hidden /> },
  // "Device" rather than "System": to a property manager, "system" is PropOS.
  { value: 'system', label: 'Device', icon: <Monitor size={15} aria-hidden /> },
];

export function ThemeControl({
  theme,
  onChange,
}: {
  readonly theme: ThemePreference;
  readonly onChange: (next: ThemePreference) => void;
}): ReactNode {
  return (
    <div
      role="group"
      aria-label="Theme"
      className="flex shrink-0 rounded-ctl border border-card-line bg-canvas p-0.5"
    >
      {OPTIONS.map((option) => {
        const active = option.value === theme;
        return (
          <button
            key={option.value}
            type="button"
            aria-label={option.value === 'system' ? 'Use device setting' : `${option.label} theme`}
            aria-pressed={active}
            onClick={() => {
              if (!active) onChange(option.value);
            }}
            className={clsx(
              'inline-flex min-h-9 items-center gap-1.5 rounded-[6px] px-2.5 text-meta font-medium',
              'transition-colors duration-(--dur-fast)',
              'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-700',
              active ? 'bg-card text-ink shadow-sm' : 'text-ink2 hover:text-ink',
            )}
          >
            {option.icon}
            <span aria-hidden className="hidden md:inline">
              {option.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
